import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchInterviewsSettings, getComplaintEnabled, getInterviewsSettings } from "store/interviewsSettingsSlice";

import * as Sentry from "@sentry/react";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { en, de } from 'yup-locales';

import infoIcon from 'assets/images/icons/info.png';
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { api } from "helpers/axios";
import { empty, errorMessage, fileDownloadResponse, formatDateTime, getTextFromHtml, isFalse, isTrue } from "helpers/common";
import sanitize from "helpers/sanitize";
import { useForm } from "react-hook-form";
import { toast } from "helpers/toast";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { Button, DateInput, Field, Input, NativeSelect } from "components/ui";
import TextTruncate from "components/ui/TextTruncate";
import EditorInput from "components/ui/EditorInput";
import ExpandableHtml from "components/ui/ExpandableHtml";
import { getLang, getModule } from "store/userSlice";
import { isDbModule } from "helpers/module";
import { getStateEmployeeSettings } from "store/stateEmployeeSlice";
dayjs.extend(customParseFormat);

function ComplaintSection({ balancedScorecardDetails, complaints, fetchBalancedScorecards, fetchComplaints, setInterviewDirty, interviewReadOnly }) {
  const lang = useSelector(getLang);

  const { translate, editableTranslate } = useTranslation();

  const [error, setError] = useState(null);

  const [complaintNotesError, setComplaintNotesError] = useState(false);

  const [selectedComplaint, setSelectedComplaint] = useState(null);

  const [complaintAddMode, setComplaintAddMode] = useState(false);

  const [complaintEditMode, setComplaintEditMode] = useState(false);

  const [complaintCommentError, setComplaintCommentError] = useState(false);

  const [complaintResolveMode, setComplaintResolveMode] = useState(false);

  const complaintSchema = yup.object({
    complaint_notes: yup.string().required()
  });

  const resolveSchema = yup.object({
    comment: yup.string().required()
  });

  const schema = useMemo(() => {
    if (complaintResolveMode) return resolveSchema;
    return complaintSchema;
  }, [complaintResolveMode]);

  const { register, watch, getValues, setValue, handleSubmit, reset, formState: { errors, isDirty } } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  const watchComplaintNotes = watch('complaint_notes');
  const watchComment = watch('comment');

  useEffect(() => {
    setInterviewDirty(isDirty);
  }, [isDirty]);

  useEffect(() => {
    setComplaintNotesError(false);
  }, [complaintAddMode, complaintEditMode]);

  useEffect(() => {
    setComplaintCommentError(false);
  }, [complaintResolveMode]);

  const handleComplaintClick = (e, complaint) => {
    if (selectedComplaint?.hash_id === complaint.hash_id) {
      setSelectedComplaint(null);
    } else {
      setSelectedComplaint(complaint);
    }

    setComplaintAddMode(false);
    setComplaintEditMode(false);
  };

  const handleResolveClick = (complaint) => {
    reset({
      hash_id: complaint.hash_id,
      comment: null
    });
    setComplaintResolveMode(true);
  };

  const handleComplaintAdd = (e = null) => {
    e?.currentTarget?.blur();
    setSelectedComplaint(null);
    setComplaintAddMode(true);
    resetForm();
  };

  const handleComplaintAddCancel = () => {
    setComplaintAddMode(false);
    resetForm();
  };

  const handleComplaintEdit = (e = null, complaint) => {
    e?.currentTarget?.blur();
    reset({
      hash_id: complaint.hash_id,
      complaint_notes: complaint.complaint_notes
    });
    setComplaintEditMode(true);
  };

  const resetForm = (e = null) => {
    e?.currentTarget?.blur();
    reset({
      hash_id: null,
      complaint_notes: null
    });
    setComplaintEditMode(false);
  };

  const onSubmit = async (formData) => {
    if (!balancedScorecardDetails || complaintNotesError || empty(getTextFromHtml(sanitize(formData.complaint_notes)))) return;

    setError(null);

    formData.employee_balanced_scorecard_hash_id = balancedScorecardDetails.hash_id;

    try {
      const { status } = await api.post(`/employee-complaints`, formData);
      if (status === 200) {
        toast.success(translate('form.success.saved'));
        handleComplaintAddCancel();
        fetchBalancedScorecards(false);
        fetchComplaints();
      }
    } catch (err) {
      setError(errorMessage(err, 'save complaint'));
      Sentry.captureException(err);
    }
  };

  const onSubmitResolve = async (formData) => {
    if (!balancedScorecardDetails || complaintCommentError || empty(getTextFromHtml(sanitize(formData.comment)))) return;

    setError(null);

    try {
      const { status } = await api.post(`/employee-complaints/` + formData.hash_id + `/resolve`, formData);
      if (status === 200) {
        toast.success(translate('form.success.saved'));
        resetResolveForm();
        fetchBalancedScorecards(false);
        fetchComplaints();
      }
    } catch (err) {
      setError(errorMessage(err, 'save complaint resolution'));
      Sentry.captureException(err);
    }
  };

  const resetResolveForm = () => {
    reset({
      hash_id: null,
      comment: null
    });
    setComplaintResolveMode(false);
  };

  return <div className="mt-4">
    <div className="interview-section-div">
      <div className="interview-section-header">
        <i className="bi bi-exclamation-triangle interview-section-header-icon"></i>
        <div className="interview-section-header-title">
          <span>{editableTranslate('interview.complaint')}</span>
        </div>
        {
          !interviewReadOnly && <div>
            <Button variant="secondary" onClick={handleComplaintAdd} data-testid="create-complaint-button"><i className="bi bi-exclamation-triangle"></i> {editableTranslate('interview.create_complaint')}</Button>
          </div>
        }
      </div>
      <div className="interview-section-body">
        {
          error && <div>
            <ErrorAlert error={error} onClose={() => setError(null)} />
          </div>
        }
        <div className="table-responsive">
          <table className="table common-table bg-white" data-testid="complaints-table">
            <thead>
              <tr>
                <th></th>
                <th>{editableTranslate('interview.complaint.description')}</th>
                <th>{editableTranslate('interview.created_on')}</th>
                <th>{editableTranslate('interview.complaint.status')}</th>
              </tr>
            </thead>
            <tbody>
              {
                complaintAddMode && <>
                  <tr className="tr-separator"></tr>
                  <tr className="employee-detail-row">
                    <td colSpan={4} className="break-word">
                      <div className="employee-detail-div">
                        <form className="bg-white p-3 rounded-sm" onSubmit={handleSubmit(onSubmit)}>
                          <div className="d-flex justify-content-between">
                            <span className="font-weight-bold text-big align-self-center">{editableTranslate('interview.create_complaint')}</span>
                            <div>
                              <Button variant="secondary" onClick={handleComplaintAddCancel} data-testid="cancel-button" className="mr-2">{editableTranslate('button.cancel')}</Button>
                              <Button type="submit" variant="primary" data-testid="save-button">{editableTranslate('button.save')}</Button>
                            </div>
                          </div>
                          <hr />
                          <div>
                            <div className="form-group">
                              <EditorInput data-testid="complaint-notes-input"
                                className={`max-height-200 ${errors?.complaint_notes ? 'is-invalid-quill' : ''}`}
                                value={watchComplaintNotes}
                                onChangeCallback={(v) => setValue('complaint_notes', v, { shouldDirty: true, shouldValidate: true })}
                                validate={(v) => v ? setComplaintNotesError(true) : setComplaintNotesError(false)} />
                            </div>
                          </div>
                        </form>
                      </div>
                    </td>
                  </tr>
                </>
              }
              {
                complaints.map((complaint, i) => <Fragment key={i}>
                  <tr className="tr-separator" data-testid={`row-${complaint?.hash_id}`}></tr>
                  <tr className={`table-row ${selectedComplaint?.hash_id === complaint.hash_id ? 'selected-employee-row' : ''}`} onClick={(e) => handleComplaintClick(e, complaint)} data-testid={`row-${complaint?.hash_id}`}>
                    <td className="text-center v-middle w-1px">
                      <i className={`open-row-icon bi bi-chevron-${selectedComplaint?.hash_id === complaint.hash_id ? 'up' : 'down'}`}></i>
                    </td>
                    <td className="break-word"><TextTruncate text={getTextFromHtml(sanitize(complaint.complaint_notes))} className="ws-normal single-line" showTooltip={false} /></td>
                    <td><TextTruncate text={formatDateTime(complaint.complaint_at, true)} /></td>
                    <td>
                      {
                        complaint.resolved_at ? <span className={`badge badge-pill badge-green`} data-tooltip-id="complaint-resolved-tooltip-at" data-tooltip-content={translate('tooltips.complaint.resolved_at', 'Beschwerde beigelegt am') + ' ' + formatDateTime(complaint.resolved_at, true)}>{editableTranslate('interview.complaint.resolved')}</span> :
                          <span className={`badge badge-pill badge-blue-2`}>{editableTranslate('interview.complaint.open')}</span>
                      }
                    </td>
                  </tr>
                  {
                    selectedComplaint?.hash_id === complaint.hash_id && <tr className={`employee-detail-row`}>
                      <td colSpan={4} className={complaintResolveMode || complaintEditMode ? 'break-word' : ''}>
                        <div className="employee-detail-div">
                          <form className="bg-white p-3 rounded-sm" onSubmit={(!complaintEditMode && !complaintResolveMode) ? null : handleSubmit(complaintEditMode ? onSubmit : onSubmitResolve)}>
                            <div className="d-flex justify-content-between">
                              <span className="font-weight-bold text-big align-self-center">{editableTranslate(complaintResolveMode ? 'interview.complaint.resolution' : 'interview.complaint_overview')}</span>
                              {
                                (!interviewReadOnly && !complaintResolveMode && !complaint.is_locked && !complaint.resolved_at) && <div>
                                  {
                                    !complaintEditMode ? <Button variant="secondary" onClick={(e) => handleComplaintEdit(e, complaint)} data-testid="edit-button" className="mr-2"><i className="bi bi-pencil-square text-normal"></i> {editableTranslate('global.edit')}</Button>
                                      : <>
                                        <Button variant="secondary" onClick={resetForm} data-testid="cancel-button" className="mr-2">{editableTranslate('button.cancel')}</Button>
                                        <Button type="submit" variant="primary" data-testid="save-button">{editableTranslate('button.save')}</Button>
                                      </>
                                  }
                                </div>
                              }
                              {
                                complaintResolveMode && <div>
                                  <Button variant="secondary" onClick={resetResolveForm} data-testid="cancel-button" className="mr-2">{editableTranslate('button.cancel')}</Button>
                                  <Button type="submit" variant="primary" data-testid="save-button">{editableTranslate('button.save')}</Button>
                                </div>
                              }
                            </div>
                            <hr />
                            {
                              complaintResolveMode && <div>
                                <div className="form-group">
                                  <Field label={editableTranslate('interview.complaint.comment')}>
                                    <EditorInput data-testid="complaint-comment-input"
                                      className={`max-height-200 ${errors?.comment ? 'is-invalid-quill' : ''}`}
                                      value={watchComment}
                                      onChangeCallback={(v) => setValue('comment', v, { shouldDirty: true, shouldValidate: true })}
                                      validate={(v) => v ? setComplaintCommentError(true) : setComplaintCommentError(false)} />
                                  </Field>
                                </div>
                              </div>
                            }
                            {
                              (!complaintResolveMode && !complaintEditMode) && <>
                                <div className="row">
                                  <div className="col-6">
                                    <div className="d-flex">
                                      <span className="interview-label">{editableTranslate('interview.complaint.status')}</span>
                                      {
                                        complaint.resolved_at ? <span data-tooltip-id="complaint-resolved-tooltip-at" data-tooltip-content={translate('tooltips.complaint.resolved_at', 'Beschwerde beigelegt am') + ' ' + formatDateTime(complaint.resolved_at, true)}>{editableTranslate('interview.complaint.resolved')}</span> :
                                          <span>{editableTranslate('interview.complaint.open')}</span>
                                      }
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="d-flex">
                                      <span className="interview-label">{editableTranslate('interview.created_on')}</span>
                                      <span>{formatDateTime(complaint.complaint_at, true)}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col">
                                    <div className="mt-3">
                                      <span className="interview-label">{editableTranslate('interview.complaint.description')}</span>
                                      <ExpandableHtml html={complaint.complaint_notes} />
                                    </div>
                                  </div>
                                </div>
                                {
                                  (!interviewReadOnly && !complaint.resolved_at) && <div className="text-right mt-4">
                                    <Button variant="secondary" onClick={() => handleResolveClick(complaint)} data-testid="resolve-complaint-button">{editableTranslate('interview.complaint.resolve')}</Button>
                                  </div>
                                }
                                {
                                  complaint.comment && <div className="row">
                                    <div className="col">
                                      <div className="mt-3">
                                        <span className="interview-label">{editableTranslate('interview.complaint.comment')}</span>
                                        <ExpandableHtml html={complaint.comment} />
                                      </div>
                                    </div>
                                  </div>
                                }
                              </>
                            }
                            {
                              (!complaintResolveMode && complaintEditMode) && <div>
                                <div className="form-group">
                                  <EditorInput data-testid="complaint-notes-input"
                                    className={`max-height-200 ${errors?.complaint_notes ? 'is-invalid-quill' : ''}`}
                                    value={watchComplaintNotes}
                                    onChangeCallback={(v) => setValue('complaint_notes', v, { shouldDirty: true, shouldValidate: true })}
                                    validate={(v) => v ? setComplaintNotesError(true) : setComplaintNotesError(false)} />
                                </div>
                              </div>
                            }
                          </form>
                        </div>
                      </td>
                    </tr>
                  }
                </Fragment>)
              }
              {
                (!complaintAddMode && (!complaints || complaints.length === 0)) && <tr>
                  <td colSpan={4} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>;
}

export default function InterviewMode({ balancedScorecardDetails, fetchBalancedScorecards, fetchBalancedScorecardDetails, reviewerView, setInterviewDirty, interviews, fetchInterviews, complaints, fetchComplaints, isSecondaryEvaluator = false, stateEvaluationFlow = {}, compactMode = false }) {
  const dispatch = useDispatch();

  const lang = useSelector(getLang);

  const { translate, editableTranslate } = useTranslation();

  const interviewsSettings = useSelector(getInterviewsSettings);
  const complaintEnabled = useSelector(getComplaintEnabled);

  const stateEmployeeSettings = useSelector(getStateEmployeeSettings);

  const currentModule = useSelector(getModule);

  const isDbModuleActive = useMemo(() => {
    return isDbModule(currentModule);
  }, [currentModule]);

  const [error, setError] = useState(null);

  const [options, setOptions] = useState([]);

  const [interviewNotesError, setInterviewNotesError] = useState(false);

  const [displayAddInterview, setDisplayAddInterview] = useState(false);

  const [selectedInterview, setSelectedInterview] = useState(null);

  const [interviewEditMode, setInterviewEditMode] = useState(false);

  const [editInterviewName, setEditInterviewName] = useState(null);

  const [loadingShare, setLoadingShare] = useState(false);

  const interviewReadOnly = useMemo(() => {
    if (reviewerView) return true;

    if (balancedScorecardDetails &&
      (!isDbModuleActive && balancedScorecardDetails?.evaluation_completed_at) &&
      balancedScorecardDetails?.balanced_scorecard_setting?.settings?.completedBehaviour !== 'meetings_can_still_be_updated') return true;

    if (isDbModuleActive) {
      if (!isSecondaryEvaluator && stateEvaluationFlow?.current_step === 'primary_meeting') return false;
      else if (isSecondaryEvaluator && stateEvaluationFlow?.current_step === 'secondary_meeting') return false;
      else return true;
    }

    return false;
  }, [balancedScorecardDetails, reviewerView, isDbModuleActive, stateEvaluationFlow, isSecondaryEvaluator]);

  const oneInterviewSetting = useMemo(() => {
    if (interviewsSettings && interviewsSettings.length === 1) return interviewsSettings[0];
    return null;
  }, [interviewsSettings]);

  const oneInterviewData = useMemo(() => {
    if (oneInterviewSetting && interviews) {
      for (const item of interviews) {
        if (item?.interview_setting?.hash_id === oneInterviewSetting?.hash_id) return item;
      }
    }
    return null;
  }, [interviews, oneInterviewSetting]);

  const completionDateAuto = useMemo(() => {
    if (isDbModuleActive &&
      !empty(stateEmployeeSettings?.state_employee_interview_completion_date_auto) &&
      isFalse(stateEmployeeSettings?.state_employee_interview_completion_date_auto)) return false;
    return true;
  }, [isDbModuleActive, stateEmployeeSettings]);

  const notesOptional = useMemo(() => {
    if (isDbModuleActive && isTrue(stateEmployeeSettings?.state_employee_interview_notes_optional)) {
      return true;
    }
    return false;
  }, [isDbModuleActive, stateEmployeeSettings]);

  const interviewSchema = useMemo(() => {
    return yup.object({
      interview_setting_hash_id: yup.string().required(),
      interview_notes: notesOptional ? yup.string().notRequired() : yup.string().required(),
      is_completed: yup.boolean().notRequired(),
      interview_date: yup.string().test('valid-date', 'Invalid Date', val => {
        if (!val || val.trim() === '') return true;
        return dayjs(val, 'DD.MM.YYYY').isValid();
      }).when('is_completed', {
        is: true,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired()
      }),
    });
  }, [notesOptional]);

  const { register, watch, getValues, setValue, handleSubmit, reset, formState: { errors, isDirty } } = useForm({
    resolver: yupResolver(interviewSchema)
  });

  const watchInterviewNotes = watch('interview_notes');
  const watchIsCompleted = watch('is_completed');

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    if (!interviewsSettings) dispatch(fetchInterviewsSettings());

    if (balancedScorecardDetails) {
      fetchInterviews();
      fetchComplaints();
    }
  }, []);

  useEffect(() => {
    const temp = [];

    interviewsSettings.forEach(setting => {
      let found = false;
      for (const interview of interviews) {
        if (interview.interview_setting.hash_id === setting.hash_id) {
          found = true;
          break;
        }
      }
      if (!found) temp.push(setting);
    });

    setOptions(temp);
  }, [interviewsSettings, interviews]);

  useEffect(() => {
    setInterviewDirty(isDirty);
  }, [isDirty]);

  useEffect(() => () => setInterviewDirty(false), []);

  useEffect(() => {
    setInterviewNotesError(false);
  }, [displayAddInterview, interviewEditMode]);

  useEffect(() => {
    if (compactMode) {
      if (oneInterviewSetting && (!oneInterviewData || !oneInterviewData?.is_completed)) {
        handleInterviewEdit(null, oneInterviewData);
      }
    }
  }, [compactMode, oneInterviewSetting, oneInterviewData]);

  const onSubmit = async (formData) => {
    const newFormData = { ...formData };

    if (!balancedScorecardDetails || interviewNotesError) return;
    if (!notesOptional && empty(getTextFromHtml(sanitize(newFormData?.interview_notes)))) return;

    setError(null);

    newFormData.employee_hash_id = balancedScorecardDetails.employee.hash_id;
    newFormData.employee_balanced_scorecard_hash_id = balancedScorecardDetails.hash_id;
    newFormData.is_completed = newFormData.is_completed || false;
    newFormData.interview_date = newFormData.interview_date ? dayjs(newFormData.interview_date, 'DD.MM.YYYY').format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');

    if (!notesOptional) {
      for (const setting of interviewsSettings) {
        if (setting.hash_id === newFormData.interview_setting_hash_id) {
          const notesLength = newFormData.interview_notes.replace(/<(.|\n)*?>/g, '').trim().length;
          if (notesLength < setting.min_length) {
            setError(translate('interview.min_characters_error') + ' ' + setting.min_length);
            return;
          }
        }
      }
    }

    try {
      const { status } = await api.post(`/employee-interviews`, newFormData);
      if (status === 200) {
        toast.success(translate('form.success.saved'));
        fetchInterviews();
        resetForm();
        fetchBalancedScorecards(false);
        fetchBalancedScorecardDetails(false);
      }
    } catch (err) {
      setError(errorMessage(err, 'save employee interview'));
      Sentry.captureException(err);
    }
  };

  const resetForm = (e = null) => {
    e?.currentTarget?.blur();
    setError(null);
    setEditInterviewName(null);
    reset({
      interview_setting_hash_id: null,
      interview_notes: null,
      is_completed: false,
      interview_date: dayjs().format('DD.MM.YYYY')
    });
    setDisplayAddInterview(false);
    setInterviewEditMode(false);
  };

  const handleInterviewClick = (interview) => {
    if (selectedInterview?.hash_id === interview.hash_id) {
      setSelectedInterview(null);
    } else {
      setSelectedInterview(interview);
    }
    setDisplayAddInterview(false);
    setInterviewEditMode(false);
  };

  const handleInterviewAdd = () => {
    setSelectedInterview(null);
    resetForm();
    setDisplayAddInterview(true);
  };

  const handleInterviewEdit = (e = null, interview) => {
    e?.currentTarget?.blur();
    if (interview) {
      const dateOnly = interview?.interview_date?.split(' ')[0] || null;
      setEditInterviewName(interview.interview_setting.name);
      reset({
        interview_setting_hash_id: interview.interview_setting.hash_id,
        interview_notes: interview.interview_notes,
        is_completed: interview.is_completed || false,
        interview_date: (dateOnly && interview?.is_completed && !completionDateAuto) ? dayjs(dateOnly, 'YYYY-MM-DD').format('DD.MM.YYYY') : dayjs().format('DD.MM.YYYY')
      });
    } else if (oneInterviewSetting) {
      // same as add
      reset({
        interview_setting_hash_id: oneInterviewSetting?.hash_id,
        interview_notes: null,
        is_completed: false,
        interview_date: dayjs().format('DD.MM.YYYY')
      });
    }
    setInterviewEditMode(true);
  };

  const handleShareInterview = async (selected) => {
    setError(null);
    setLoadingShare(true);

    try {
      const response = await api.post('/exports/employee-balanced-scorecard-interview', {
        balanced_scorecard_hash_id: balancedScorecardDetails.hash_id,
        interview_hash_id: selected?.hash_id
      }, { responseType: 'blob' });

      fileDownloadResponse(response, 'employee-balanced-scorecard-interview.pdf');
      // toast.success(translate('interview.share_to_email_success'));
    } catch (err) {
      // setError(errorMessage(err, 'share employee balanced scorecard interviews'));
      // Sentry.captureException(err);
      let errObj = { message: 'Something went wrong.' };

      // if blob has error
      if (err.response && err.response.data instanceof Blob) {
        try {
          console.log('trying to parse blob error');
          const text = await err.response.data.text();
          errObj = JSON.parse(text);
        } catch (e) {
          // ignore
        }
      } else if (err.response && err.response.data) {
        errObj = err.response.data;
      }

      setError(errorMessage(errObj, 'share employee balanced scorecard interviews'));
      Sentry.captureException(errObj);
    }
    setLoadingShare(false);
  };

  return <>
    <div className={`${compactMode ? 'mt-3' : 'mt-4'}`}>
      {
        oneInterviewSetting ? <div>
          {
            error && <div>
              <ErrorAlert error={error} onClose={() => setError(null)} />
            </div>
          }

          {
            compactMode ? <div>
              <h5>{interviewsSettings[0]?.name}
                {
                  (oneInterviewData?.is_completed && !isDbModuleActive) && <>
                    <Button variant="ghost" size="inline" className="ml-2" icon="bi-envelope" data-tooltip-id="one-interview-icon-tooltip" onClick={() => handleShareInterview(oneInterviewData)} disabled={loadingShare || interviews.length < 1} data-testid="share-interview-button" />
                    <ReactTooltip id="one-interview-icon-tooltip" place="bottom" content={editableTranslate('interview.share_with_employee', 'An Beschäftigte senden')} />
                  </>
                }
              </h5>

              {
                (interviewEditMode && !oneInterviewData?.is_completed) ? <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="form-group mb-2">
                    <EditorInput data-testid="interview-notes-input"
                      className={`max-height-200 ${errors?.interview_notes ? 'is-invalid-quill' : ''}`}
                      value={watchInterviewNotes}
                      onChangeCallback={(v) => setValue('interview_notes', v, { shouldDirty: true, shouldValidate: true })}
                      validate={(v) => v ? setInterviewNotesError(true) : setInterviewNotesError(false)} />
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="form-check form-check-inline">
                        <input className="form-check-input no-mt" type="checkbox" {...register('is_completed')} id="interview-edit-completed-checkbox" data-testid="interview-edit-completed-checkbox" />
                        <label className="form-check-label" htmlFor="interview-edit-completed-checkbox">{editableTranslate('interview.mark_as_completed')}</label>
                      </div>
                      <div className={`d-inline-flex w-auto ${(watchIsCompleted && !completionDateAuto) ? '' : 'invisible'}`}>
                        <DateInput className="bg-white w-120" error={!!errors?.interview_date}
                          {...register('interview_date')} testId="interview-date-input" />
                      </div>
                    </div>
                    <Button type="submit" variant="secondary" data-testid="save-button">{editableTranslate('button.save')}</Button>
                  </div>
                </form> : <>
                  <div>
                    <div>
                      <span>
                        <span className="font-weight-bold">{editableTranslate('interview.created_on')}:</span> {(oneInterviewData && oneInterviewData?.is_completed) ? formatDateTime(oneInterviewData.interview_date, true) : '-'}
                      </span>
                    </div>
                    <div className="mt-2">
                      {
                        (oneInterviewData && !empty(getTextFromHtml(oneInterviewData.interview_notes))) ? <ExpandableHtml html={oneInterviewData.interview_notes} /> : '-'
                      }
                    </div>
                  </div>
                </>
              }
            </div> :
              <form className={`interview-section-div ${(!oneInterviewData?.is_completed && !interviewReadOnly && isDbModuleActive) ? 'interview-box-shadow' : ''}`} onSubmit={!interviewEditMode ? null : handleSubmit(onSubmit)}>
                <div className="interview-section-header">
                  <i className="bi bi-journal interview-section-header-icon"></i>
                  <div className="interview-section-header-title">
                    <span className="v-middle">{interviewsSettings[0]?.name} {oneInterviewData?.is_completed && <i className={`bi bi-check-circle-fill ml-2`}></i>}</span>
                  </div>
                  <div>
                    {
                      (oneInterviewData && !interviewEditMode && oneInterviewData?.is_completed && !isDbModuleActive) && <Button variant="secondary" onClick={() => handleShareInterview(oneInterviewData)} loading={loadingShare} disabled={interviews.length < 1} data-testid="share-button">
                        {
                          <span>
                            {!loadingShare && <i className="bi bi-envelope"></i>} {editableTranslate('interview.share_with_employee', 'An Beschäftigte senden')}
                          </span>
                        }
                      </Button>
                    }
                    {
                      !interviewReadOnly && <>
                        {
                          !interviewEditMode ? <Button variant="secondary" onClick={(e) => handleInterviewEdit(e, oneInterviewData)} data-testid="edit-button" className="ml-2"><i className="bi bi-pencil-square text-normal"></i> {editableTranslate('global.edit')}</Button>
                            : <>
                              <Button variant="secondary" onClick={resetForm} data-testid="cancel-button" className="ml-2 mr-2">{editableTranslate('button.cancel')}</Button>
                              <Button type="submit" variant="primary" data-testid="save-button">{editableTranslate('button.save')}</Button>
                            </>
                        }
                      </>
                    }
                  </div>
                </div>
                <div className="interview-section-body">
                  {
                    interviewEditMode ? <>
                      <div className="form-group">
                        <Field label={editableTranslate('interview.interview_notes')}>
                          <EditorInput data-testid="interview-notes-input"
                            className={`max-height-200 ${interviewReadOnly ? 'is-invalid-quill' : ''} ${errors?.interview_notes ? 'is-invalid-quill' : ''}`}
                            value={watchInterviewNotes}
                            onChangeCallback={(v) => setValue('interview_notes', v, { shouldDirty: true, shouldValidate: true })}
                            validate={(v) => v ? setInterviewNotesError(true) : setInterviewNotesError(false)}
                            readOnly={interviewReadOnly} />
                        </Field>
                      </div>
                      <div className="form-check form-check-inline">
                        <input className="form-check-input no-mt" type="checkbox" {...register('is_completed')} id="interview-edit-completed-checkbox" data-testid="interview-edit-completed-checkbox" />
                        <label className="form-check-label" htmlFor="interview-edit-completed-checkbox">{editableTranslate('interview.mark_as_completed')}</label>
                      </div>
                      <div className={`d-inline-flex w-auto ${(watchIsCompleted && !completionDateAuto) ? '' : 'invisible'}`}>
                        <DateInput className="bg-white w-120" error={!!errors?.interview_date}
                          {...register('interview_date')} testId="interview-date-input" />
                      </div>
                    </> : <>
                      <div className="d-flex">
                        <span className="interview-label">{editableTranslate('interview.created_on')}</span>
                        <span>{(oneInterviewData && oneInterviewData?.is_completed) ? formatDateTime(oneInterviewData.interview_date, true) : '-'}</span>
                      </div>
                      <div className="mt-3">
                        <span className="interview-label">{editableTranslate('interview.interview_notes')}</span>
                        <div>
                          {
                            (oneInterviewData && !empty(getTextFromHtml(oneInterviewData.interview_notes))) ? <ExpandableHtml html={oneInterviewData.interview_notes} /> : '-'
                          }
                        </div>
                      </div>
                    </>
                  }
                </div>
              </form>
          }
        </div> :
          <div className="interview-section-div">
            <div className="interview-section-header">
              <i className="bi bi-journal interview-section-header-icon"></i>
              <div className="interview-section-header-title">
                <span>{editableTranslate('interview.section_title', 'Personalgespräch')}</span>
              </div>
              <div>
                {
                  (!displayAddInterview && options.length > 0 && !interviewReadOnly) && <Button variant="secondary" onClick={handleInterviewAdd} data-testid="add-button"><i className="bi bi-plus"></i> {editableTranslate('interview.add_interview')}</Button>
                }
              </div>
            </div>
            <div className="interview-section-body">
              {
                error && <div>
                  <ErrorAlert error={error} onClose={() => setError(null)} />
                </div>
              }

              <div className="table-responsive">
                <table className="table common-table bg-white" data-testid="options-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>{editableTranslate('interview.name')}</th>
                      <th>{editableTranslate('interview.interview_notes')}</th>
                      <th className="text-center">{editableTranslate('interview.completed')}</th>
                      <th>{editableTranslate('interview.created_on')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      displayAddInterview && <>
                        <tr className="tr-separator"></tr>
                        <tr className="employee-detail-row">
                          <td colSpan={5} className="break-word">
                            <div className="employee-detail-div">
                              <form className="bg-white p-3 rounded-sm" onSubmit={handleSubmit(onSubmit)}>
                                <div className="d-flex justify-content-between">
                                  <span className="font-weight-bold text-big align-self-center">{editableTranslate('interview.add_interview')}</span>
                                  <div>
                                    <Button variant="secondary" onClick={resetForm} data-testid="cancel-button" className="mr-2">{editableTranslate('button.cancel')}</Button>
                                    <Button type="submit" variant="primary" data-testid="save-button">{editableTranslate('button.save')}</Button>
                                  </div>
                                </div>
                                <hr />
                                <div>
                                  {
                                    editInterviewName ? <div className="form-group">
                                      <Field label={editableTranslate('interview.interview_name')}>
                                        <Input data-testid="interview-name-input" type="text" className="w-auto" value={editInterviewName} readOnly />
                                      </Field>
                                    </div> : <div className="form-group">
                                      <Field label={editableTranslate('interview.interview_name')}>
                                      <NativeSelect className="w-auto" error={!!errors?.interview_setting_hash_id} {...register('interview_setting_hash_id')} data-testid="interview-setting-hash-id-select">
                                        <option value=""></option>
                                        {
                                          options.map((opt, i) => <option key={i} value={opt.hash_id}>{opt.name}</option>)
                                        }
                                      </NativeSelect>
                                      </Field>
                                    </div>
                                  }
                                  <div className="form-group">
                                    <Field label={editableTranslate('interview.interview_notes')}>
                                      <EditorInput data-testid="interview-notes-input"
                                        className={`max-height-200 ${interviewReadOnly ? 'is-invalid-quill' : ''} ${errors?.interview_notes ? 'is-invalid-quill' : ''}`}
                                        value={watchInterviewNotes}
                                        onChangeCallback={(v) => setValue('interview_notes', v, { shouldDirty: true, shouldValidate: true })}
                                        validate={(v) => v ? setInterviewNotesError(true) : setInterviewNotesError(false)}
                                        readOnly={interviewReadOnly} />
                                    </Field>
                                  </div>
                                  <div className="form-check form-check-inline">
                                    <input className="form-check-input no-mt" type="checkbox" {...register('is_completed')} id="interview-add-completed-checkbox" data-testid="interview-add-completed-checkbox" />
                                    <label className="form-check-label" htmlFor="interview-add-completed-checkbox">{editableTranslate('interview.mark_as_completed')}</label>
                                  </div>
                                  <div className={`d-inline-flex w-auto ${(watchIsCompleted && !completionDateAuto) ? '' : 'invisible'}`}>
                                    <DateInput className="bg-white w-120" error={!!errors?.interview_date}
                                      {...register('interview_date')} testId="interview-date-input" />
                                  </div>
                                </div>
                              </form>
                            </div>
                          </td>
                        </tr>
                      </>
                    }
                    {
                      interviews.map((interview, i) => {
                        return <Fragment key={i}>
                          <tr className="tr-separator" data-testid={`row-${interview?.hash_id}`}></tr>
                          <tr className={`table-row ${selectedInterview?.hash_id === interview.hash_id ? 'selected-employee-row' : ''}`} onClick={() => handleInterviewClick(interview)} data-testid={`row-${interview?.hash_id}`}>
                            <td className="text-center v-middle w-1px">
                              <i className={`open-row-icon bi bi-chevron-${selectedInterview?.hash_id === interview.hash_id ? 'up' : 'down'}`}></i>
                            </td>
                            <td><TextTruncate text={interview.interview_setting.name} /></td>
                            <td className="break-word"><TextTruncate text={getTextFromHtml(sanitize(interview.interview_notes))} className="ws-normal single-line" showTooltip={false} /></td>
                            <td className="text-center">{interview.is_completed && <i className={`bi bi-check-circle-fill`}></i>}</td>
                            <td>{interview.is_completed ? <TextTruncate text={formatDateTime(interview.interview_date, true)} /> : ''}</td>
                          </tr>
                          {
                            selectedInterview?.hash_id === interview.hash_id && <tr className={`employee-detail-row`}>
                              <td colSpan={5} className={interviewEditMode ? 'break-word' : ''}>
                                <div className="employee-detail-div">
                                  <form className="bg-white p-3 rounded-sm" onSubmit={!interviewEditMode ? null : handleSubmit(onSubmit)}>
                                    <div className="d-flex justify-content-between">
                                      <span className="font-weight-bold text-big align-self-center">{editableTranslate('interview.interview_overview')}</span>
                                      <div>
                                        {
                                          (!interviewEditMode && !isDbModuleActive) && <Button variant="secondary" onClick={() => handleShareInterview(selectedInterview)} loading={loadingShare} disabled={interviews.length < 1} data-testid="share-button">
                                            {
                                              <span>
                                                {!loadingShare && <i className="bi bi-envelope"></i>} {editableTranslate('interview.share_with_employee', 'An Beschäftigte senden')}
                                              </span>
                                            }
                                          </Button>
                                        }
                                        {
                                          !interviewReadOnly && <>
                                            {
                                              !interviewEditMode ? <Button variant="secondary" onClick={(e) => handleInterviewEdit(e, interview)} data-testid="edit-button" className="ml-2"><i className="bi bi-pencil-square text-normal"></i> {editableTranslate('global.edit')}</Button>
                                                : <>
                                                  <Button variant="secondary" onClick={resetForm} data-testid="cancel-button" className="ml-2 mr-2">{editableTranslate('button.cancel')}</Button>
                                                  <Button type="submit" variant="primary" data-testid="save-button">{editableTranslate('button.save')}</Button>
                                                </>
                                            }
                                          </>
                                        }
                                      </div>
                                    </div>
                                    <hr />
                                    {
                                      !interviewEditMode && <>
                                        <div className="row">
                                          <div className="col-6">
                                            <div className="d-flex">
                                              <span className="interview-label">{editableTranslate('interview.name')}</span>
                                              <span>{interview.interview_setting.name}</span>
                                            </div>
                                          </div>
                                          <div className="col-6">
                                            <div className="d-flex">
                                              <span className="interview-label">{editableTranslate('interview.created_on')}</span>
                                              <span>{interview.is_completed ? formatDateTime(interview.interview_date, true) : '-'}</span>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="row">
                                          <div className="col">
                                            <div className="mt-3">
                                              <span className="interview-label">{editableTranslate('interview.interview_notes')}</span>
                                              <div>
                                                {!empty(getTextFromHtml(interview.interview_notes)) ? <ExpandableHtml html={interview.interview_notes} /> : '-'}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </>
                                    }
                                    {
                                      interviewEditMode && <div>
                                        {
                                          editInterviewName ? <div className="form-group">
                                            <Field label={editableTranslate('interview.interview_name')}>
                                              <Input data-testid="interview-name-input" type="text" className="w-auto" value={editInterviewName} readOnly />
                                            </Field>
                                          </div> : <div className="form-group">
                                            <Field label={editableTranslate('interview.interview_name')}>
                                            <NativeSelect className="w-auto" error={!!errors?.interview_setting_hash_id} {...register('interview_setting_hash_id')} data-testid="interview-setting-hash-id-select">
                                              <option value=""></option>
                                              {
                                                options.map((opt, i) => <option key={i} value={opt.hash_id}>{opt.name}</option>)
                                              }
                                            </NativeSelect>
                                            </Field>
                                          </div>
                                        }
                                        <div className="form-group">
                                          <Field label={editableTranslate('interview.interview_notes')}>
                                            <EditorInput data-testid="interview-notes-input"
                                              className={`max-height-200 ${interviewReadOnly ? 'is-invalid-quill' : ''} ${errors?.interview_notes ? 'is-invalid-quill' : ''}`}
                                              value={watchInterviewNotes}
                                              onChangeCallback={(v) => setValue('interview_notes', v, { shouldDirty: true, shouldValidate: true })}
                                              validate={(v) => v ? setInterviewNotesError(true) : setInterviewNotesError(false)}
                                              readOnly={interviewReadOnly} />
                                          </Field>
                                        </div>
                                        <div className="form-check form-check-inline">
                                          <input className="form-check-input no-mt" type="checkbox" {...register('is_completed')} id="interview-edit-completed-checkbox" data-testid="interview-edit-completed-checkbox" />
                                          <label className="form-check-label" htmlFor="interview-edit-completed-checkbox">{editableTranslate('interview.mark_as_completed')}</label>
                                        </div>
                                        <div className={`d-inline-flex w-auto ${(watchIsCompleted && !completionDateAuto) ? '' : 'invisible'}`}>
                                          <DateInput className="bg-white w-120" error={!!errors?.interview_date}
                                            {...register('interview_date')} testId="interview-date-input" />
                                        </div>
                                      </div>
                                    }
                                  </form>
                                </div>
                              </td>
                            </tr>
                          }
                        </Fragment>
                      })
                    }
                    {
                      (!interviews || interviews.length === 0) && <tr>
                        <td colSpan={5} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </div>
      }
    </div>
    {
      complaintEnabled && <ComplaintSection balancedScorecardDetails={balancedScorecardDetails} complaints={complaints} fetchBalancedScorecards={fetchBalancedScorecards} fetchComplaints={fetchComplaints} setInterviewDirty={setInterviewDirty} interviewReadOnly={interviewReadOnly} />
    }

    <ReactTooltip id="complaint-resolve-tooltip" place="bottom">{translate('tooltips.complaint.resolve')}</ReactTooltip>
    <ReactTooltip id="complaint-resolved-tooltip-at" place="bottom" />
  </>
}
